import "@total-typescript/ts-reset"

export {}

declare global {
  const ENVIRONMENT_NAME: string
  const BUNDLE_REF: string
  const GRAPH_QL: string
  const __IS_TEST__: boolean
  const PUBLIC_URL: string
  const API_GW: string
  const IS_LOCAL: boolean

  interface PBError extends Error {
    apiError?: any
    statusCode: number
    errorCode: string
    errorData: Record<string, string>
    traceId: string
    response?: any
    config?: any
  }

  interface RawGraphError extends PBError {
    networkError?: PBError
  }

  interface GraphError extends PBError {
    apiError: any
    operationName?: string
    formattedMessage?: string
  }

  /**
   * The Prettify helper is a utility type that takes an object type and makes the hover overlay more readable.
   *
   * -- [totaltypescript](https://www.totaltypescript.com/concepts/the-prettify-helper)
   */
  type Prettify<T> = {
    [K in keyof T]: T[K]
  } & {}

  /**
   * Make one or more fields of an interface required, similar to `Required`, but targets individual keys
   *
   * @example
   * interface Loose {
   *   name?: string
   *   uuid?: string
   *   age?: number
   * }
   *
   * type Identified = WithRequired<Loose, "name" | "uuid">
   *
   * const a: Loose = {} // => ok
   * const b: Identified = {} // => type error, missing properties
   * const c: Identified = {name: "foo", uuid: "bar"} // => ok
   */
  type WithRequired<T, K extends keyof T> = Prettify<T & {[P in K]-?: T[P]}>

  /**
   * Make one or more fields of an interface optional, similar to `Partial`, but targets individual keys
   *
   * @example
   * interface User {
   *   uuid: string
   *   name: string
   *   age: number
   * }
   *
   * type NewUser = WithPartial<User, "name" | "age">
   *
   * const a: User = { uuid: '1' } // => type error, missing properties
   * const b: NewUser = { uuid: '1'} // => ok
   */
  type WithPartial<T, K extends keyof T> = Prettify<Omit<T, K> & Partial<Pick<T, K>>>

  /**
   * Deeply make all fields of an object optional
   * @link [TotalTypescript](https://www.totaltypescript.com/tips/use-deep-partials-to-help-with-mocking-an-entity)
   */
  type DeepPartial<Thing> = Thing extends (...args: any[]) => any
    ? Thing
    : Thing extends Array<infer InferredArrayMember>
      ? DeepPartialArray<InferredArrayMember>
      : Thing extends object
        ? DeepPartialObject<Thing>
        : Thing | undefined

  type DeepPartialArray<Thing> = Array<DeepPartial<Thing>>

  type DeepPartialObject<Thing> = {
    [Key in keyof Thing]?: DeepPartial<Thing[Key]>
  }

  /**
   * Remove readonly modifier from a given type
   */
  type Writeable<T> = {-readonly [P in keyof T]: T[P]}

  /**
   * Deeply remove readonly modifier from a given type
   */
  type DeepWriteable<T> = {-readonly [P in keyof T]: DeepWriteable<T[P]>}
}
